/**
 * Real, in-person AI tutors, mostly across the South West and South of England.
 * Shown as silhouette placeholders for now (a privacy placeholder while
 * availability is confirmed — no real photos are rendered). Each tutor carries
 * tags so visitors can filter by what they want to learn, who they work best
 * with, and their sector.
 *
 * Shared by the homepage map and the one-to-one finder on /train-your-team, so
 * the pin positions and the data never drift apart.
 */

/** What a tutor can teach. */
export const TUTOR_FOCUS = [
  "AI fundamentals",
  "Prompt writing",
  "Business & productivity",
  "Automation & workflows",
  "Marketing & content",
  "Data & analysis",
] as const;

/** Who a tutor works best with (the "type of person"). */
export const TUTOR_BEST_FOR = [
  "Complete beginners",
  "Leaders & managers",
  "Customer-facing teams",
  "Operations & admin",
  "Technical teams",
  "Creative teams",
] as const;

/** Sectors a tutor knows well. */
export const TUTOR_INDUSTRIES = [
  "Professional services",
  "Finance & accountancy",
  "Retail & e-commerce",
  "Hospitality & tourism",
  "Healthcare & care",
  "Manufacturing & engineering",
  "Public sector & education",
  "Creative & media",
] as const;

export type TutorFocus = (typeof TUTOR_FOCUS)[number];
export type TutorBestFor = (typeof TUTOR_BEST_FOR)[number];
export type TutorIndustry = (typeof TUTOR_INDUSTRIES)[number];

export interface Tutor {
  town: string;
  county: string;
  /** Links to that area's local-training page. */
  href: string;
  lat: number;
  lng: number;
  focus: readonly TutorFocus[];
  bestFor: readonly TutorBestFor[];
  industries: readonly TutorIndustry[];
}

export const TUTORS: readonly Tutor[] = [
  {
    town: "Truro",
    county: "Cornwall",
    href: "/cornwall/truro",
    lat: 50.26,
    lng: -5.05,
    focus: ["AI fundamentals", "Business & productivity"],
    bestFor: ["Complete beginners", "Customer-facing teams"],
    industries: ["Hospitality & tourism", "Retail & e-commerce"],
  },
  {
    town: "Plymouth",
    county: "Devon",
    href: "/devon/plymouth",
    lat: 50.37,
    lng: -4.14,
    focus: ["Automation & workflows", "Data & analysis"],
    bestFor: ["Operations & admin", "Technical teams"],
    industries: ["Manufacturing & engineering", "Public sector & education"],
  },
  {
    town: "Exeter",
    county: "Devon",
    href: "/devon/exeter",
    lat: 50.72,
    lng: -3.53,
    focus: ["Prompt writing", "Marketing & content"],
    bestFor: ["Creative teams", "Leaders & managers"],
    industries: ["Creative & media", "Professional services"],
  },
  {
    town: "Bristol",
    county: "Bristol",
    href: "/bristol/bristol",
    lat: 51.45,
    lng: -2.59,
    focus: ["AI fundamentals", "Automation & workflows", "Data & analysis"],
    bestFor: ["Technical teams", "Leaders & managers"],
    industries: ["Professional services", "Finance & accountancy"],
  },
  {
    town: "Cheltenham",
    county: "Gloucestershire",
    href: "/gloucestershire/cheltenham",
    lat: 51.9,
    lng: -2.07,
    focus: ["Business & productivity", "Data & analysis"],
    bestFor: ["Leaders & managers", "Operations & admin"],
    industries: ["Finance & accountancy", "Professional services"],
  },
  {
    town: "Salisbury",
    county: "Wiltshire",
    href: "/wiltshire/salisbury",
    lat: 51.07,
    lng: -1.79,
    focus: ["AI fundamentals", "Prompt writing"],
    bestFor: ["Complete beginners", "Operations & admin"],
    industries: ["Public sector & education", "Healthcare & care"],
  },
  {
    town: "Bournemouth",
    county: "Dorset",
    href: "/dorset/bournemouth",
    lat: 50.72,
    lng: -1.88,
    focus: ["Marketing & content", "Business & productivity"],
    bestFor: ["Customer-facing teams", "Creative teams"],
    industries: ["Retail & e-commerce", "Hospitality & tourism"],
  },
  {
    town: "Southampton",
    county: "Hampshire",
    href: "/hampshire/southampton",
    lat: 50.91,
    lng: -1.4,
    focus: ["Automation & workflows", "Business & productivity"],
    bestFor: ["Operations & admin", "Leaders & managers"],
    industries: ["Manufacturing & engineering", "Retail & e-commerce"],
  },
  {
    town: "Brighton",
    county: "East Sussex",
    href: "/east-sussex/brighton",
    lat: 50.82,
    lng: -0.14,
    focus: ["Prompt writing", "Marketing & content", "AI fundamentals"],
    bestFor: ["Creative teams", "Complete beginners"],
    industries: ["Creative & media", "Healthcare & care"],
  },
];

// The relief map's documented extent (Wikimedia file description):
// equirectangular, N 61.0, S 49.0, W -11.0, E 2.2 (the 170% N/S stretch is
// baked into the image aspect, so latitude still maps linearly to the vertical
// axis). Keep these in lockstep with the map image.
export const MAP_BOUNDS = { n: 61.0, s: 49.0, w: -11.0, e: 2.2 } as const;
export const tutorXPct = (lng: number) =>
  ((lng - MAP_BOUNDS.w) / (MAP_BOUNDS.e - MAP_BOUNDS.w)) * 100;
export const tutorYPct = (lat: number) =>
  ((MAP_BOUNDS.n - lat) / (MAP_BOUNDS.n - MAP_BOUNDS.s)) * 100;
